import {
  AuthorisedUser,
  ClearingHouseType,
  CollectionType,
  DefaultHeading,
  Order,
  OrderActions,
  OrderCreateArgs,
  ProductSector,
  SponsorType,
  SpreadsheetMapping,
  SpreadsheetMappingType,
  TradingEntity
} from 'api'
import { Locale, Translation } from 'locale'
import { isEmpty } from 'lodash'
import { enabledCollectionTypes } from 'model'
import React, { PropsWithChildren } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { clearingHouseIdByTypeGetterSelector, sponsorIdByTypeGetterSelector } from 'selectors/clearingSelectors'
import { createAvailableCollectionIdsByNameAndTypeGetterSelector } from 'selectors/collectionSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createOrdersWithCollectionIdGetterSelector } from 'selectors/orderSelectors'
import { canUploadPositionsSelector } from 'selectors/permissionSelectors'
import { createSortedSpreadsheetMappingsSelector } from 'selectors/spreadsheetMappingSelectors'
import { allActiveTradingEntitiesSelector } from 'selectors/tradingEntitySelectors'
import { excelService, ioService } from 'services'
import { isNotEmpty, isNotNil } from 'utils/predicate'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { Dropzone } from 'view/shared/upload'
import { getDefaultMapping, parseClipboard } from '../upload/model'
import { spreadsheetMappingSortByLastSetAsDefaultPredicate } from '../upload/utils'
import { OrderUploadDialog } from './OrderUploadDialog'
import { OrderSpreadsheetParseResult, parseOrderSpreadsheet } from './parseOrderSpreadsheet'
import { MAX_CREATE_ORDER_SPREADSHEET_CONTENT_LENGTH } from './utils'
import { productSectorByIsinBuiGetterSelector } from 'selectors'

interface OrderDropzoneProviderProps extends ModalConsumerProps {
  canUploadPositions: boolean
  authorisedUser: AuthorisedUser
  getAvailableCollectionIdsByName: (name: string, type: CollectionType | null) => string[]
  getOrdersInCollection: (collectionId: string) => Order[]
  getClearingHouseIdByType: (clearingHouse: ClearingHouseType) => string | null
  getSponsorIdByType: (sponsor: SponsorType) => string | null
  getProductSectorById: (collateralId: string) => ProductSector | null
  allActiveTradingEntities: TradingEntity[]
  onCreateOrders: (args: OrderCreateArgs) => unknown
  spreadsheetMappings: SpreadsheetMapping[]
}

const mapStateToProps = createStructuredSelector({
  canUploadPositions: canUploadPositionsSelector,
  authorisedUser: createAuthorisedUserSelector(),
  getAvailableCollectionIdsByName: createAvailableCollectionIdsByNameAndTypeGetterSelector,
  getOrdersInCollection: createOrdersWithCollectionIdGetterSelector,
  getClearingHouseIdByType: clearingHouseIdByTypeGetterSelector,
  getSponsorIdByType: sponsorIdByTypeGetterSelector,
  getProductSectorById: productSectorByIsinBuiGetterSelector,
  allActiveTradingEntities: allActiveTradingEntitiesSelector,
  spreadsheetMappings: createSortedSpreadsheetMappingsSelector(() => SpreadsheetMappingType.STAGING)
})

const mapDispatchToProps = {
  onCreateOrders: OrderActions.createWithResponse
}

class OrderDropzoneProviderComponent extends React.Component<PropsWithChildren<OrderDropzoneProviderProps>> {
  render() {
    const { children, canUploadPositions } = this.props
    if (!canUploadPositions) {
      return children
    }

    return (
      <Dropzone render={() => <>{children}</>} dataTestId="OrderDropzone" onDropFiles={this.handleDropFiles} onPaste={this.handlePaste} />
    )
  }

  parseData = (data: unknown[], filename: string): OrderSpreadsheetParseResult => {
    const {
      authorisedUser,
      getAvailableCollectionIdsByName,
      getOrdersInCollection,
      getClearingHouseIdByType,
      getSponsorIdByType,
      getProductSectorById,
      allActiveTradingEntities,
      spreadsheetMappings
    } = this.props
    const [userSelectedMapping] = spreadsheetMappings.sort(spreadsheetMappingSortByLastSetAsDefaultPredicate)
    const defaultMapping = getDefaultMapping(SpreadsheetMappingType.STAGING)

    return parseOrderSpreadsheet(
      filename,
      data,
      userSelectedMapping || defaultMapping,
      authorisedUser,
      getAvailableCollectionIdsByName,
      getOrdersInCollection,
      enabledCollectionTypes,
      getClearingHouseIdByType,
      getSponsorIdByType,
      getProductSectorById,
      allActiveTradingEntities
    )
  }

  createOrders = (parseResult: OrderSpreadsheetParseResult, data: unknown[]) => {
    const { onCreateOrders } = this.props
    onCreateOrders({
      deleteCurrentOrdersInStaging: false,
      orderAttributes: parseResult.parsedItems.reverse(),
      spreadsheetContent: JSON.stringify(data).slice(0, MAX_CREATE_ORDER_SPREADSHEET_CONTENT_LENGTH),
      ignoreAxeId: null,
      performUserValidations: false
    })
  }

  handleDropFiles = async (files: File[]) => {
    if (isNotNil(files[0])) {
      const fileData = await ioService.readFile(files[0])
      const data = await excelService.parseXlsx(fileData, { fileType: files[0].type })
      const parseResult = this.parseData(data, files[0].name)

      if (isEmpty(parseResult.issues) && isNotEmpty(parseResult.parsedItems)) {
        this.createOrders(parseResult, data)
      } else {
        this.props.showModal(<OrderUploadDialog initialFile={files[0]} />)
      }
    }
  }

  handlePaste = (clipboard: string) => {
    if (isNotEmpty(clipboard)) {
      const data = parseClipboard(clipboard, {
        dynamicTyping: (field: string) => (field === DefaultHeading.IsinBui ? false : true)
      })
      const parseResult = this.parseData(data, Locale.translate(Translation.SC_CLIPBOARD))

      if (isEmpty(parseResult.issues) && isNotEmpty(parseResult.parsedItems)) {
        this.createOrders(parseResult, data)
      } else {
        this.props.showModal(<OrderUploadDialog initialClipboard={clipboard} />)
      }
    }
  }
}

export const OrderDropzoneProvider = withModal(connect(mapStateToProps, mapDispatchToProps)(OrderDropzoneProviderComponent))
